import { Shield, Crown, Briefcase, Building, Users } from "lucide-react";
import {
  PageHeading, Section, Callout,
  Diagram, MockTable,
} from "../shared";

const LANES = [
  { key: "EDGE_CHAIRMAN", label: "EDGE Chairman", icon: Crown, color: "#a855f7", description: "Items addressed to or sponsored by the EDGE Chairman. An above-MD lane — items go through Awaiting Dispatch after SDF MD approval." },
  { key: "EDGE_MD_OFFICE", label: "EDGE MD Office", icon: Briefcase, color: "#3b82f6", description: "Items routed through the EDGE Managing Director's office. An above-MD lane — the Awaiting Dispatch step applies." },
  { key: "BOARD_EXCOM", label: "Board / ExCom", icon: Building, color: "#10b981", description: "Items needing board or executive committee attention. An above-MD lane — the most senior governance path." },
  { key: "SDF_MD", label: "SDF MD", icon: Users, color: "#f59e0b", description: "Items handled at the SDF MD level — closed by the SDF MD without an upward dispatch step." },
];

export default function LanesSection() {
  return (
    <div data-testid="guide-lanes">
      <PageHeading
        icon={Shield}
        title="Authority Lanes"
        description="Every action item runs on exactly one 'lane'. The lane says which part of the organisation owns the item and which approval path it follows."
      />

      <Callout variant="info" title="In plain words">
        A <span className="text-white">lane</span> is like a track at a train station. Each item is put on one track, and the track decides who reviews it and how far up the organisation it travels before it's done.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          You choose a lane when you register an item. After that, the lane quietly controls the rest of the journey. The Summary page groups all active items by their lane so you can see everything on each track at a glance.
        </p>
      </Section>

      <Section title="The four lanes">
        <Diagram
          title="The lanes and where each one ends"
          frameLabel="Authority Lanes"
          legend={[
            <><span className="text-white">EDGE Chairman</span> — items for the EDGE Chairman. Travels above the MD.</>,
            <><span className="text-white">EDGE MD Office</span> — items through the EDGE Managing Director's office. Travels above the MD.</>,
            <><span className="text-white">Board / ExCom</span> — board or executive committee items. The most senior path.</>,
            <><span className="text-white">SDF MD</span> — stays within SDF and is closed by the MD directly.</>,
          ]}
        >
          <MockTable
            columns={["Lane", "What it's for", "Goes above the MD?"]}
            rows={LANES.map((lane, i) => {
              const Icon = lane.icon;
              return [
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-100 text-[10px] font-bold items-center justify-center ring-1 ring-cyan-400/50">{i + 1}</span>
                  <Icon className="w-3.5 h-3.5" style={{ color: lane.color }} />
                  <span className="text-white/80">{lane.label}</span>
                </span>,
                lane.description,
                lane.key === "SDF_MD" ? <span className="text-white/50">No — closed by the MD</span> : <span className="text-amber-300">Yes — Awaiting Dispatch</span>,
              ];
            })}
          />
        </Diagram>
        <p className="text-xs text-white/40">
          Each lane has its own reviewers and sign-off authorities, set up by the Super Admin.
        </p>
      </Section>

      <Section title="Above-MD lanes vs the SDF MD lane">
        <p className="text-sm text-white/65 leading-relaxed">
          Three lanes are <span className="text-white">above-MD</span>: EDGE Chairman, EDGE MD Office, and Board / ExCom. Items on these tracks aren't closed by the SDF MD — when the MD approves them, they move to <span className="text-yellow-300">Awaiting Dispatch</span> until the PMO or Board Secretary sends them upward.
        </p>
        <p className="text-sm text-white/65 leading-relaxed">
          Items on the <span className="text-white">SDF MD</span> track stay within SDF and are closed directly by the MD's review.
        </p>
        <Callout variant="info">
          Lanes are configurable. Your Super Admin may have added or renamed lanes under <span className="text-white">Admin → Lanes</span>. The "above-MD" group is decided by the lane's internal key, not its display name.
        </Callout>
      </Section>
    </div>
  );
}
